import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, RotateCcw, AlertTriangle, Calendar, Building2, FileText, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";

function Papelera({ user, onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("casos");
  const [casos, setCasos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purgeTarget, setPurgeTarget] = useState(null); // { kind: 'caso'|'evento', id, title }
  const [purgePassword, setPurgePassword] = useState("");

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [c, e] = await Promise.all([
        api.get("/papelera"),
        api.get("/papelera/eventos")
      ]);
      setCasos(c.data || []);
      setEventos(e.data || []);
    } catch { toast.error("Error cargando papelera"); }
    finally { setLoading(false); }
  };

  const restaurarCaso = async (id) => {
    try { await api.post(`/papelera/${id}/restaurar`); toast.success("Caso restaurado"); loadAll(); }
    catch { toast.error("Error restaurando"); }
  };
  const restaurarEvento = async (id) => {
    try { await api.post(`/papelera/eventos/${id}/restaurar`); toast.success("Hecho restaurado"); loadAll(); }
    catch { toast.error("Error restaurando"); }
  };

  const purgar = async () => {
    if (!purgePassword) { toast.error("Ingresa la clave"); return; }
    try {
      if (purgeTarget.kind === "caso") {
        await api.delete(`/papelera/${purgeTarget.id}/purgar?password=${encodeURIComponent(purgePassword)}`);
      } else {
        await api.delete(`/papelera/eventos/${purgeTarget.id}/purgar?password=${encodeURIComponent(purgePassword)}`);
      }
      toast.success("Eliminado permanentemente");
      setPurgeTarget(null);
      setPurgePassword("");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error eliminando");
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: "var(--primary)" }} />
    </div>
  );

  const total = casos.length + eventos.length;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="papelera-page">
      <TopNav user={user} onLogout={onLogout} />

      <main className="container mx-auto px-6 py-6 max-w-6xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--accent-rose-light)" }}>
            <Trash2 className="h-5 w-5" style={{ color: "var(--accent-rose)" }} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ color: "var(--text)" }}>Papelera de Reciclaje</h1>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>{total} elemento{total !== 1 ? "s" : ""} en papelera</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab("casos")} className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === "casos" ? "text-white" : ""}`} style={{ background: tab === "casos" ? "var(--primary)" : "var(--surface)", color: tab === "casos" ? "white" : "var(--text-secondary)" }} data-testid="tab-papelera-casos">
            <Building2 className="h-3.5 w-3.5 inline mr-1.5" />Casos · {casos.length}
          </button>
          <button onClick={() => setTab("eventos")} className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === "eventos" ? "text-white" : ""}`} style={{ background: tab === "eventos" ? "var(--primary)" : "var(--surface)", color: tab === "eventos" ? "white" : "var(--text-secondary)" }} data-testid="tab-papelera-eventos">
            <FileText className="h-3.5 w-3.5 inline mr-1.5" />Hechos · {eventos.length}
          </button>
        </div>

        {tab === "casos" && (casos.length === 0 ? (
          <div className="ls-card p-12 text-center" style={{ borderStyle: "dashed" }}>
            <Trash2 className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--text-light)" }} />
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>No hay casos en papelera</p>
          </div>
        ) : (
          <div className="space-y-2">
            {casos.map(caso => (
              <div key={caso.id} className="ls-card px-5 py-4" data-testid={`trash-caso-${caso.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--accent-rose-light)" }}>
                    <span className="text-sm font-bold" style={{ color: "var(--accent-rose)" }}>#{caso.numero_caso || "—"}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold truncate" style={{ color: "var(--text)" }}>{caso.titulo_caso}</h3>
                    <div className="flex items-center gap-3 text-[11px]" style={{ color: "var(--text-muted)" }}>
                      <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{caso.empresa_nombre}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{caso.fecha_eliminacion ? new Date(caso.fecha_eliminacion).toLocaleDateString("es-CL") : "—"}</span>
                      {caso.eliminado_por && <><span>·</span><span>{caso.eliminado_por}</span></>}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => restaurarCaso(caso.id)} className="h-8 text-xs gap-1.5" style={{ color: "var(--primary)", borderColor: "var(--primary-muted)" }} data-testid={`restore-caso-${caso.id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Restaurar
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setPurgeTarget({ kind: "caso", id: caso.id, title: caso.titulo_caso }); setPurgePassword(""); }} className="h-8 text-xs gap-1.5 text-red-500 border-red-200 hover:bg-red-50" data-testid={`purge-caso-${caso.id}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Purgar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

        {tab === "eventos" && (eventos.length === 0 ? (
          <div className="ls-card p-12 text-center" style={{ borderStyle: "dashed" }}>
            <Trash2 className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--text-light)" }} />
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>No hay hechos en papelera</p>
          </div>
        ) : (
          <div className="space-y-2">
            {eventos.map(ev => (
              <div key={ev.id} className="ls-card px-5 py-4" data-testid={`trash-evento-${ev.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--accent-rose-light)" }}>
                    <FileText className="h-4 w-4" style={{ color: "var(--accent-rose)" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold truncate" style={{ color: "var(--text)" }}>{ev.titulo_evento}</h3>
                    <div className="flex items-center gap-3 text-[11px]" style={{ color: "var(--text-muted)" }}>
                      <span className="flex items-center gap-1"><Hash className="h-3 w-3" />Caso #{ev.caso_numero} · {ev.caso_titulo}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{ev.fecha_eliminacion ? new Date(ev.fecha_eliminacion).toLocaleDateString("es-CL") : "—"}</span>
                      {ev.eliminado_por && <><span>·</span><span>{ev.eliminado_por}</span></>}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => restaurarEvento(ev.id)} className="h-8 text-xs gap-1.5" style={{ color: "var(--primary)", borderColor: "var(--primary-muted)" }} data-testid={`restore-evento-${ev.id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Restaurar
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setPurgeTarget({ kind: "evento", id: ev.id, title: ev.titulo_evento }); setPurgePassword(""); }} className="h-8 text-xs gap-1.5 text-red-500 border-red-200 hover:bg-red-50" data-testid={`purge-evento-${ev.id}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Purgar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </main>

      <Dialog open={!!purgeTarget} onOpenChange={() => setPurgeTarget(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Eliminar permanentemente
            </DialogTitle>
            <DialogDescription>
              {purgeTarget?.kind === "caso" ? "Esta acción no se puede deshacer. Se eliminarán todos los datos asociados al caso." : "Esta acción no se puede deshacer. El hecho se borrará para siempre."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>Elemento: <strong>{purgeTarget?.title}</strong></p>
            <Input
              type="password"
              placeholder="Clave de seguridad"
              value={purgePassword}
              onChange={(e) => setPurgePassword(e.target.value)}
              data-testid="purge-password-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setPurgeTarget(null)}>Cancelar</Button>
              <Button onClick={purgar} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-purge-btn">
                Eliminar para siempre
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Papelera;
